import { useState } from 'react';
import type { Bbox } from './types';
import { bboxToString } from './types';
import { navigate } from './navigate';
import { useStore } from './state/store';
import { isStandalone } from './platform';

/** "Use my location": ask the browser where the user is and open the map around them. */

export interface UserLocation {
  lat: number;
  lon: number;
  accuracyM: number;
  ts: number;
}

/** Half-width of the area opened around the user — a ~150 km view, enough to see local arrivals and departures. */
const AROUND_KM = 75;

export const LAST_AREA_KEY = 'planesight:lastArea';

/** Open the live map on an area (and remember it as the last one used). */
export function goToArea(bbox: Bbox, name: string | null): void {
  try {
    localStorage.setItem(LAST_AREA_KEY, JSON.stringify({ bbox, name }));
  } catch {
    /* private mode */
  }
  const params = new URLSearchParams({ bbox: bboxToString(bbox) });
  if (name) params.set('name', name);
  navigate(`/map?${params.toString()}`);
}

export function areaAround(lat: number, lon: number, km = AROUND_KM): Bbox {
  const dLat = km / 111.32;
  const dLon = km / (111.32 * Math.max(Math.cos((lat * Math.PI) / 180), 0.05));
  return {
    lamin: Math.max(lat - dLat, -85),
    lamax: Math.min(lat + dLat, 85),
    lomin: Math.max(lon - dLon, -180),
    lomax: Math.min(lon + dLon, 180),
  };
}

/** Why a location request failed — the UI shows different help for each. */
export type LocationProblem = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

export class LocationError extends Error {
  constructor(
    readonly kind: LocationProblem,
    message: string,
    /** What the browser had remembered when it failed ('denied' = this page was refused before). */
    readonly permission: PermissionState | 'unknown' = 'unknown',
    /** One line for troubleshooting: the raw error code and the environment. */
    readonly detail: string | null = null,
  ) {
    super(message);
  }
}

const CODE_NAMES: Record<number, string> = { 1: 'PERMISSION_DENIED', 2: 'POSITION_UNAVAILABLE', 3: 'TIMEOUT' };

function locationError(err: GeolocationPositionError, permission: PermissionState | 'unknown'): LocationError {
  const detail = [
    `permission: ${permission}`,
    `error ${err.code} ${CODE_NAMES[err.code] ?? ''}`.trim(),
    err.message ? `"${err.message}"` : null,
    `installed app: ${isStandalone() ? 'yes' : 'no'}`,
    `v${__APP_VERSION__}`,
  ]
    .filter(Boolean)
    .join(' · ');
  if (err.code === err.PERMISSION_DENIED) {
    return new LocationError('denied', 'Location is turned off for PlaneSight.', permission, detail);
  }
  if (err.code === err.TIMEOUT) return new LocationError('timeout', 'Finding your location took too long.', permission, detail);
  return new LocationError('unavailable', "Your phone couldn't work out where you are.", permission, detail);
}

/** Browser's remembered answer to "share location with this site?", where it can be read. */
export async function locationPermission(): Promise<PermissionState | 'unknown'> {
  try {
    return (await navigator.permissions.query({ name: 'geolocation' })).state;
  } catch {
    return 'unknown';
  }
}

/** One-shot position from the browser (prompts for permission the first time). */
export function locateUser(): Promise<UserLocation> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator) || !window.isSecureContext) {
      reject(new LocationError('unsupported', "This browser can't share its location with this page."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracyM: p.coords.accuracy, ts: p.timestamp }),
      (e) => {
        void locationPermission().then((state) => reject(locationError(e, state)));
      },
      // City-level accuracy is plenty for a ~150 km view; this answers fast and
      // doesn't wake up GPS on phones.
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 5 * 60_000 },
    );
  });
}

/** Button state for "use my location": locating flag, last error, and the action. */
export function useLocateMe(): {
  locate: () => void;
  locating: boolean;
  error: string | null;
  errorKind: LocationProblem | null;
  errorPermission: PermissionState | 'unknown';
  errorDetail: string | null;
  clearError: () => void;
} {
  const setUserLocation = useStore((s) => s.setUserLocation);
  const [locating, setLocating] = useState(false);
  const [problem, setProblem] = useState<LocationError | null>(null);
  const locate = () => {
    if (locating) return;
    setLocating(true);
    setProblem(null);
    locateUser()
      .then((loc) => {
        setUserLocation(loc);
        goToArea(areaAround(loc.lat, loc.lon), 'My location');
      })
      .catch((err: unknown) =>
        setProblem(err instanceof LocationError ? err : new LocationError('unavailable', "Couldn't get your location.")),
      )
      .finally(() => setLocating(false));
  };
  return {
    locate,
    locating,
    error: problem?.message ?? null,
    errorKind: problem?.kind ?? null,
    errorPermission: problem?.permission ?? 'unknown',
    errorDetail: problem?.detail ?? null,
    clearError: () => setProblem(null),
  };
}
